import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, Trash2, Download, RefreshCw, Search, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function AdminNewsletter() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/newsletter");
      setSubscribers(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Loading failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id, email) => {
    if (!window.confirm(`Remove ${email} from newsletter?`)) return;
    try {
      await api.delete(`/admin/newsletter/${id}`);
      toast.success("Subscriber removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = subscribers.filter((s) => !q || s.email.toLowerCase().includes(q.toLowerCase()));

  const exportCsv = () => {
    const header = "email,language,created_at\n";
    const rows = filtered.map((s) => `${s.email},${s.language || "fr"},${s.created_at || ""}`).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `newsletter-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const copyAll = () => {
    const emails = filtered.map((s) => s.email).join(", ");
    navigator.clipboard.writeText(emails);
    toast.success(`${filtered.length} emails copied`);
  };

  return (
    <div className="space-y-6" data-testid="admin-newsletter-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[var(--dd-green)]">Newsletter subscribers</h1>
          <p className="text-sm text-[var(--dsf-text-secondary)] mt-1">
            {subscribers.length} total · {filtered.length} matching filter
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} className="text-sm flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--dsf-border)] hover:bg-[var(--dd-bone)]" data-testid="newsletter-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={copyAll} disabled={!filtered.length} className="text-sm flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--dsf-border)] hover:bg-[var(--dd-bone)] disabled:opacity-40" data-testid="newsletter-copy-all">
            <Copy size={14} /> Copy all emails
          </button>
          <button onClick={exportCsv} disabled={!filtered.length} className="btn-mustard !py-2.5 disabled:opacity-40" data-testid="newsletter-export-csv">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[var(--dsf-border)] p-4 flex items-center gap-3">
        <Search size={16} className="text-[var(--dsf-text-muted)]" />
        <Input
          placeholder="Search by email…"
          value={q} onChange={(e) => setQ(e.target.value)}
          className="border-0 shadow-none focus-visible:ring-0"
          data-testid="newsletter-search"
        />
      </div>

      <div className="bg-white rounded-2xl border border-[var(--dsf-border)] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-[var(--dsf-text-secondary)]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="newsletter-empty">
            <Mail className="mx-auto text-[var(--dd-green)] mb-3" size={32} />
            <div className="font-semibold text-[var(--dd-green)]">No subscribers yet</div>
            <div className="text-sm text-[var(--dsf-text-secondary)] mt-1">
              When customers sign up via the footer form, they will appear here.
            </div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--dd-bone)]">
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[var(--dd-green)] font-bold">
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Language</th>
                <th className="px-4 py-3">Subscribed</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-t border-[var(--dsf-border)] hover:bg-[var(--dd-bone)]/40" data-testid={`newsletter-row-${s.id}`}>
                  <td className="px-4 py-3 font-medium text-[var(--dd-green)]">{s.email}</td>
                  <td className="px-4 py-3 text-xs uppercase tracking-wider text-[var(--dsf-text-secondary)]">{s.language || "fr"}</td>
                  <td className="px-4 py-3 text-xs text-[var(--dsf-text-muted)]">{s.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(s.id, s.email)} className="text-[var(--dd-coral)] hover:opacity-70" data-testid={`newsletter-delete-${s.id}`}>
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
